/* eslint-disable no-param-reassign -- gate entries and three.js objects are mutable records updated in place */
import * as THREE from 'three'
import type { Reflector } from 'three/examples/jsm/objects/Reflector'

import type { Entry, PreparedReflector } from './types'

const prepared = new WeakMap<Reflector, PreparedReflector>()
const entryOf = new WeakMap<Reflector, Entry>()

export const getPreparedReflector = (reflector: Reflector) =>
  prepared.get(reflector)

export const setReflectorEntry = (reflector: Reflector, entry: Entry) => {
  entryOf.set(reflector, entry)
}

export const clearReflectorEntry = (reflector: Reflector) => {
  entryOf.delete(reflector)
}

/**
 * Swaps the reflector onto its fallback material and wraps its render hooks
 * so a reflection pass only runs when the gate has allowed it. Safe to call
 * during render, before the reflector is registered with any gate.
 */
export function prepareReflector(
  reflector: Reflector,
  fallbackColor: THREE.ColorRepresentation
): void {
  if (prepared.has(reflector)) return

  const record: PreparedReflector = {
    baseBeforeRender: reflector.onBeforeRender,
    baseAfterRender: reflector.onAfterRender,
    reflectorMaterial: reflector.material as THREE.Material,
    fallbackMaterial: new THREE.MeshStandardMaterial({
      color: fallbackColor,
      metalness: 1,
      roughness: 0.15,
    }),
  }
  prepared.set(reflector, record)
  reflector.material = record.fallbackMaterial

  let hiddenDuringRender: THREE.Object3D[] = []
  let didRender = false

  const hideObject = (object: THREE.Object3D) => {
    if (object !== reflector && object.visible) {
      object.visible = false
      hiddenDuringRender.push(object)
    }
  }

  reflector.onBeforeRender = (
    ...args: Parameters<Reflector['onBeforeRender']>
  ) => {
    didRender = false
    const entry = entryOf.get(reflector)
    if (!entry || !entry.allowed) return
    entry.allowed = false

    const [, , camera] = args

    hiddenDuringRender = []
    entry.host.hideDuringPass(hideObject)
    record.baseBeforeRender.apply(reflector, args)

    entry.host.snapshot(entry, camera)
    entry.renderedOnce = true
    entry.framesSinceRender = 0
    didRender = true
  }

  reflector.onAfterRender = (
    ...args: Parameters<Reflector['onAfterRender']>
  ) => {
    record.baseAfterRender.apply(reflector, args)
    if (!didRender) return
    hiddenDuringRender.forEach((other) => {
      other.visible = true
    })
    hiddenDuringRender = []
  }
}

/** Undoes `prepareReflector`, restoring the original material and hooks. */
export function restoreReflector(reflector: Reflector): void {
  const record = prepared.get(reflector)
  if (!record) return
  reflector.material = record.reflectorMaterial
  record.fallbackMaterial.dispose()
  reflector.onBeforeRender = record.baseBeforeRender
  reflector.onAfterRender = record.baseAfterRender
  prepared.delete(reflector)
}
